import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreateUser, useRoles } from "../hooks/useUsers";
import type { UserDetail, UserStatus } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (user: UserDetail) => void;
};

export function NewUserDrawer({ isOpen, onClose, onCreated }: Props) {
  const createUser = useCreateUser();
  const { data: roles = [] } = useRoles();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState<UserStatus>("ACTIVE");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName("");
    setEmail("");
    setPassword("");
    setConfirm("");
    setStatus("ACTIVE");
    setAvatarUrl("");
    setRoleIds([]);
    setError(null);
  }, [isOpen]);

  const toggleRole = (id: string) => {
    setRoleIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (roleIds.length === 0) {
      setError("Please assign at least one role");
      return;
    }

    try {
      const user = await createUser.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        password,
        roleIds,
        status,
        avatarUrl: avatarUrl.trim() ? avatarUrl.trim() : null,
      });
      onCreated?.(user);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">New User</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
          autoComplete="off"
        >
          {/* Honeypot pair — Chrome ignores autoComplete="off" on
              password inputs paired with username-ish fields, but
              reliably autofills the FIRST username/password pair it
              sees. These hidden, focusless dummies absorb the autofill
              so the real fields below stay empty. Mirrors the Vault
              and Profile patterns. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              opacity: 0,
              height: 0,
              width: 0,
              pointerEvents: "none",
            }}
          />
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              opacity: 0,
              height: 0,
              width: 0,
              pointerEvents: "none",
            }}
          />

          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Basic Information
            </h3>

            <div className="space-y-2">
              <Label htmlFor="user-name">
                Full name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="user-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter full name"
                required
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                data-1p-ignore="true"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-email">
                Email address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="user-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                required
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                data-1p-ignore="true"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="user-password">
                  Password <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="user-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-confirm">
                  Confirm password <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="user-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-status">Status</Label>
              <Select
                id="user-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as UserStatus)}
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-avatar">Avatar URL</Label>
              <Input
                id="user-avatar"
                type="url"
                placeholder="https://…"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Assign roles <span className="text-destructive">*</span>
              </Label>
              <div
                className="grid gap-2 rounded-md border p-3"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                {roles.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No roles available. Please seed roles first.
                  </p>
                ) : (
                  roles.map((role) => (
                    <label
                      key={role.id}
                      className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-muted/40"
                    >
                      <Checkbox
                        checked={roleIds.includes(role.id)}
                        onCheckedChange={() => toggleRole(role.id)}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">{role.name}</div>
                        {role.description && (
                          <div className="text-xs text-muted-foreground">
                            {role.description}
                          </div>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !name.trim() ||
                !email.trim() ||
                !password ||
                !confirm ||
                roleIds.length === 0 ||
                createUser.isPending
              }
            >
              {createUser.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              Create User
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
